import React from 'react';

import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { REGEX } from '@lessonPlanner/regex';

import { Button } from '../Button';

import { useCharacterLimit } from '../../hooks/useCharacterLimit';

import { formClasses } from './utils/FormStyles';
import { Label } from './utils/Label';
import { SubLabel } from './utils/SubLabel';

interface TextAreaFieldProps {
  id: string;
  label?: string;
  maxLength?: number;
  disabled?: boolean;
  isMaxLengthReached?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  textAreaRef?: React.RefObject<HTMLTextAreaElement>;
  subLabel?: string;
  showSubLabel?: boolean;
  errorMessage?: string;
  resizable?: boolean;
  link?: string;
  onPaste?: (text: string) => void;
  showPasteButton?: boolean;
  className?: string;
  value?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  [key: string]: unknown;
}

export function TextAreaField({
  id,
  label = '',
  maxLength = 300,
  disabled = false,
  isMaxLengthReached = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onPointerDown = () => {},
  textAreaRef: externalTextAreaRef = null,
  subLabel = '',
  showSubLabel = false,
  errorMessage = '',
  resizable = true,
  link = '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onPaste = () => {},
  showPasteButton = false,
  ...props
}: TextAreaFieldProps) {
  const {
    elementRef: internalTextAreaRef,
    handleChange: handleLimitedChange,
    isAtOrOverMaxLength,
    effectiveMaxLength,
  } = useCharacterLimit<HTMLTextAreaElement>({
    maxLength,
    onChange: props.onChange,
  });

  const combinedRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      if (internalTextAreaRef) {
        (
          internalTextAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node;
      }

      if (externalTextAreaRef) {
        (
          externalTextAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>
        ).current = node;
      }
    },
    [internalTextAreaRef, externalTextAreaRef],
  );

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value;

    if (!REGEX.VALID_TEXT_INPUT.test(inputValue)) {
      const cleanedInput = inputValue.replace(REGEX.INVALID_TEXT_INPUT, '');
      e.target.value = cleanedInput;
    }

    handleLimitedChange(e);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (onPaste) {
        onPaste(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  };

  const textareaClassName = clsx(
    formClasses,
    { 'cursor-not-allowed': disabled },
    {
      'border border-red-500 focus:border-red-700':
        isMaxLengthReached || errorMessage,
    },
    {
      'resize-none': !resizable,
    },
  );

  const isCurrentlyAtOrOverMaxLength = isAtOrOverMaxLength(props.value);

  return (
    <div className={clsx('relative', props.className)}>
      {label && (
        <Label id={id} link={link}>
          {label}
        </Label>
      )}
      {showSubLabel && <SubLabel id={id}>{subLabel}</SubLabel>}
      {showPasteButton && (
        <Button
          className="absolute right-1 p-2 text-gray-400 hover:text-gray-600"
          disabled={disabled}
          type="button"
          onClick={handlePaste}
        >
          <ClipboardDocumentListIcon className="ml-2 inline h-6" />
        </Button>
      )}
      <textarea
        ref={combinedRef}
        id={id}
        {...props}
        className={textareaClassName}
        disabled={disabled}
        maxLength={effectiveMaxLength}
        onChange={handleTextareaChange}
        onPointerDown={onPointerDown}
      />
      {(isMaxLengthReached || isCurrentlyAtOrOverMaxLength) && (
        <p className="text-red-500">You have reached the character limit</p>
      )}
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </div>
  );
}
